import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { productsAPI } from '../services/api';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Block as DisableIcon,
  CheckCircle as EnableIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
} from '@mui/icons-material';

const Products = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [filters, setFilters] = useState({ code: '', name: '', description: '', unit: '', status: 'all' });
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    price: '',
    unit: 'unit',
    enabled: true,
  });

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productsAPI.getAll();
      setProducts(response.data);
      setError('');
    } catch (err) {
      setError(t('products.failedToLoad'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleOpenModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        code: product.code,
        name: product.name,
        description: product.description || '',
        price: product.price,
        unit: product.unit,
        enabled: product.enabled,
      });
    } else {
      setEditingProduct(null);
      setFormData({ code: '', name: '', description: '', price: '', unit: 'unit', enabled: true });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingProduct(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({ ...formData, [name]: name === 'enabled' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, price: parseFloat(formData.price) };
      if (editingProduct) {
        await productsAPI.update(editingProduct.id, data);
      } else {
        await productsAPI.create(data);
      }
      handleCloseModal();
      fetchProducts();
    } catch (err) {
      setError(err.response?.data?.message || t('products.failedToSave'));
    }
  };

  const handleToggleStatus = async (product) => {
    try {
      if (product.enabled) {
        await productsAPI.disable(product.id);
      } else {
        await productsAPI.enable(product.id);
      }
      fetchProducts();
    } catch (err) {
      setError(t('products.failedToUpdateStatus'));
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await productsAPI.exportCsv();
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `productos-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(t('products.failedToExport'));
    } finally {
      setExporting(false);
    }
  };

  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        setImporting(true);
        const response = await productsAPI.importCsv(ev.target.result);
        setImportResult(response.data);
        fetchProducts();
      } catch (err) {
        setError(err.response?.data?.message || t('products.failedToImport'));
      } finally {
        setImporting(false);
      }
    };
    reader.readAsText(file, 'UTF-8');
  };

  const matchesText = (val, q) => (val || '').toString().toLowerCase().includes(q.trim().toLowerCase());
  const filteredProducts = products.filter((p) =>
    matchesText(p.code, filters.code) &&
    matchesText(p.name, filters.name) &&
    matchesText(p.description, filters.description) &&
    matchesText(p.unit, filters.unit) &&
    (filters.status === 'all' || (filters.status === 'enabled' ? p.enabled : !p.enabled))
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>{t('products.title')}</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined"
            startIcon={exporting ? <CircularProgress size={16} /> : <ExportIcon />}
            onClick={handleExport} disabled={exporting}>
            {exporting ? t('products.exporting') : t('products.exportCsv')}
          </Button>
          <Button variant="outlined"
            startIcon={importing ? <CircularProgress size={16} /> : <ImportIcon />}
            onClick={() => fileInputRef.current?.click()} disabled={importing}>
            {importing ? t('products.importing') : t('products.importCsv')}
          </Button>
          <input type="file" accept=".csv,text/csv" ref={fileInputRef}
            style={{ display: 'none' }} onChange={handleImportFile} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal()}>
            {t('products.addProduct')}
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600 }}>{t('products.code')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('products.name')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('products.description')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('products.price')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('products.unit')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('products.status')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('products.actions')}</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ pt: 0 }}>
                <TextField fullWidth size="small" variant="standard" placeholder={t('common.filter')}
                  value={filters.code} onChange={(e) => setFilters({ ...filters, code: e.target.value })} />
              </TableCell>
              <TableCell sx={{ pt: 0 }}>
                <TextField fullWidth size="small" variant="standard" placeholder={t('common.filter')}
                  value={filters.name} onChange={(e) => setFilters({ ...filters, name: e.target.value })} />
              </TableCell>
              <TableCell sx={{ pt: 0 }}>
                <TextField fullWidth size="small" variant="standard" placeholder={t('common.filter')}
                  value={filters.description} onChange={(e) => setFilters({ ...filters, description: e.target.value })} />
              </TableCell>
              <TableCell />
              <TableCell sx={{ pt: 0 }}>
                <TextField fullWidth size="small" variant="standard" placeholder={t('common.filter')}
                  value={filters.unit} onChange={(e) => setFilters({ ...filters, unit: e.target.value })} />
              </TableCell>
              <TableCell sx={{ pt: 0 }}>
                <TextField select fullWidth size="small" variant="standard"
                  value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                  <MenuItem value="all">{t('common.all')}</MenuItem>
                  <MenuItem value="enabled">{t('common.enabled')}</MenuItem>
                  <MenuItem value="disabled">{t('common.disabled')}</MenuItem>
                </TextField>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProducts.map((product) => (
              <TableRow key={product.id}>
                <TableCell>{product.code}</TableCell>
                <TableCell>{product.name}</TableCell>
                <TableCell>{product.description}</TableCell>
                <TableCell>${parseFloat(product.price).toFixed(2)}</TableCell>
                <TableCell>{product.unit}</TableCell>
                <TableCell>
                  <Chip
                    label={product.enabled ? t('common.enabled') : t('common.disabled')}
                    color={product.enabled ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title={t('common.edit')}>
                    <IconButton onClick={() => handleOpenModal(product)} size="small" aria-label={t('common.edit')}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={product.enabled ? t('common.disable') : t('common.enable')}>
                    <IconButton
                      onClick={() => handleToggleStatus(product)}
                      size="small"
                      color={product.enabled ? 'error' : 'success'}
                    >
                      {product.enabled ? <DisableIcon /> : <EnableIcon />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {filteredProducts.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  {t('products.noProductsMatch')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit/Create Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingProduct ? t('products.editProduct') : t('products.addNew')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField fullWidth label={t('products.productCode')} name="code"
              value={formData.code} onChange={handleChange} required margin="normal"
              disabled={!!editingProduct} />
            <TextField fullWidth label={t('products.productName')} name="name"
              value={formData.name} onChange={handleChange} required margin="normal" />
            <TextField fullWidth label={t('products.description')} name="description"
              value={formData.description} onChange={handleChange} margin="normal" multiline rows={2} />
            <TextField fullWidth label={t('products.price')} name="price" type="number"
              value={formData.price} onChange={handleChange} required margin="normal"
              inputProps={{ step: '0.01', min: '0' }} />
            <TextField fullWidth label={t('products.unit')} name="unit"
              value={formData.unit} onChange={handleChange} margin="normal" />
            <FormControlLabel
              control={<Switch name="enabled" checked={formData.enabled} onChange={handleChange} />}
              label={t('common.enabled')} sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained">
              {editingProduct ? t('common.update') : t('common.create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Import Result Dialog */}
      <Dialog open={!!importResult} onClose={() => setImportResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('products.importResults')}</DialogTitle>
        <DialogContent>
          {importResult && (
            <Box>
              <Alert severity={importResult.errors?.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
                {t('products.importSummary', {
                  created: importResult.created,
                  updated: importResult.updated,
                  errors: importResult.errors?.length ?? 0,
                })}
              </Alert>
              {importResult.errors?.length > 0 && (
                <List dense>
                  {importResult.errors.map((e, i) => (
                    <ListItem key={i}><ListItemText primary={e} /></ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResult(null)} variant="contained">{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Products;
